import React, { createContext, useContext, useEffect, useState } from 'react';

import type { ChargeTiming } from '../components/forms/ChargeTimingConsent';
import { useNotifications } from '../hooks/useNotifications';
import {
  Accommodation,
  AccommodationChildSummary,
  BillingBasis,
  CartItemExtra,
  Package,
  PackageChildSummary,
  SelectedModifier,
  Service,
  ServiceChildSummary,
  ServiceOccurrence,
  TransferDirectionValue,
} from '../types';
import { ProductType } from '../types/enums';
import { isUndatedService } from '../utils/schedulingMode';

// NextPax draft inputs that the property-detail card collects (rate plan,
// fee selections, granular guest mix) but the base CartItem does not carry.
// Stored on a NextPax accommodation item so the unified checkout can rebuild
// the property leg without the URL params the old direct-checkout path relied on.
export interface NextPaxCartDetails {
  ratePlanId: number | null;
  pets: number;
  vehicles: number;
  extraBeds: number;
  selectedFeeCodes: string[];
  currency: string;
  children: number;
  adults?: number | null;
}

export interface CartItem {
  id: string;
  type: ProductType;
  accommodation?: Accommodation;
  service?: Service;
  package?: Package;
  /** The selected child variant when booking a parent accommodation with
   *  options. Optional — absent for standalone (no-variant) accommodations. */
  accommodationVariant?: AccommodationChildSummary;
  /** The selected child tier when booking a parent package with options.
   *  Optional — absent for standalone (no-tier) packages. */
  packageVariant?: PackageChildSummary;
  // Accommodation fields (also used by a package leg — a package books a
  // single date range like a stay, not a point-in-time service).
  checkInDate?: string;
  checkOutDate?: string;
  // Service fields
  serviceDate?: string;
  serviceTime?: string;
  /** The chosen occurrence of a fixed-schedule service: a display snapshot,
   *  and the line's identity. Absent for every other service. */
  occurrence?: ServiceOccurrence;
  /** The selected child variant when booking a parent service with options.
   *  Optional — absent for standalone (no-variant) services. */
  variant?: ServiceChildSummary;
  /** Direction of travel for a chauffeur-transfer booking. Optional — absent
   *  for every non-transfer service and for accommodations. Booking metadata
   *  only; never affects price. */
  transferDirection?: TransferDirectionValue;
  // Common fields
  guestCount: number;
  /** The line's charged total, in the listing's currency. `null` for a
   *  by-request line — the price is unknown until an admin issues a quote
   *  (see `isByRequest` below). */
  totalPrice: number | null;
  specialRequests?: string;
  /** True when this line was carted from a by-request listing (or, for a
   *  service, accommodation, or package with a selected child variant, when
   *  that variant itself is by-request) OR the guest's selection itself
   *  includes a by-request modifier on an otherwise-priced listing (see
   *  `hasByRequestModifier` below). Always derived by `addToCart` from
   *  `deriveCartItemIsByRequest` — never accepted from the caller. Optional so every cart item built or
   *  persisted before this field existed keeps parsing exactly as it did
   *  (treated as `false` by every reader here); `addToCart` always stamps a
   *  concrete `true`/`false` on a fresh add, and the mount-time hydration
   *  effect below normalises an absent value to `false`. Drives the
   *  unpriced-line branches in `cartLineResolver` and the `ON_QUOTE` bucket
   *  in `deriveChargeTiming`/`getChargeTimingGroups` below. */
  isByRequest?: boolean;
  /** True when the guest's SELECTION (not the listing itself) includes a
   *  by-request modifier — the host card computes this with
   *  `selectionContainsByRequest(groups, selection)` and passes it in, since
   *  the cart item stores only `{modifier_id, quantity}` per selected entry
   *  (see `selectedModifiers` below) and cannot re-derive this fact from the
   *  selection alone without leaking catalogue data onto the persisted cart
   *  item. Optional — absent ⇒ treated as `false` by every reader,
   *  including a cart row persisted before this field existed. Read by
   *  `deriveCartItemIsByRequest`'s OR-rule for every item type (SERVICE,
   *  ACCOMMODATION, and PACKAGE alike); never mutates `selectedModifiers`
   *  or `totalPrice` itself. */
  hasByRequestModifier?: boolean;
  /** Booked duration/count for a time-unit service (hour/30min/day).
   *  Optional — absent for every other pricing unit and for accommodation
   *  and package items. Mirrors estimated_unit_count on the wire;
   *  threaded onto the checkout-cart line verbatim by
   *  cartLineResolver.toNativeCheckoutLine. */
  estimatedUnitCount?: number;
  /** Priced-extras selection for a native accommodation or service item —
   *  also carried on a by-request line when the selection itself is what
   *  makes the item by-request (see `hasByRequestModifier` above).
   *  Optional — absent for every item that carries no modifier selection.
   *  Threaded verbatim onto the checkout-cart wire by
   *  `cartLineResolver.toNativeCheckoutLine`, omitted (not sent as `[]`)
   *  when empty. */
  selectedModifiers?: SelectedModifier[];
  /** DISPLAY-ONLY snapshot of `selectedModifiers`' guest-facing labels,
   *  quantities, and by-request flags, resolved once (at add time) from the
   *  loaded `ModifierGroup[]` — e.g. by `buildSelectedExtras` in the
   *  package purchase panel. Persisted verbatim alongside the cart item
   *  (like every other field above) so a cart-rendering surface can name a
   *  line's extras without re-fetching the catalogue. Optional — absent
   *  for every item added before this field existed, or one that carries
   *  no modifier selection; a reader treats absent identically to an empty
   *  array. NEVER threaded onto the checkout wire — `toNativeCheckoutLine`
   *  sends only `selectedModifiers` above, never this snapshot. */
  selectedExtras?: CartItemExtra[];
  /** Billing mechanism carried over from the service at add time (see
   *  `Service.billing_basis`). Optional — absent for every accommodation
   *  and package item, and for a service that carries no billing_basis of
   *  its own (every service today, and every FIXED_QUOTE service). Never
   *  set directly by a caller: `addToCart` derives it from `item.service`
   *  and overwrites whatever the caller passed. Drives the cart-mix guard
   *  below — a METERED item may never share a cart with any other item. */
  billingBasis?: BillingBasis;
  // Present only on a NextPax accommodation item — the property leg the
  // unified checkout captures immediately. Absence means native (Reserve) item.
  nextPax?: NextPaxCartDetails;
  dateAdded: string;
}

// A NextPax accommodation is the single "pay today" property leg; native
// accommodations and services are "Reserve" (deferred-charge) items.
const isNextPaxAccommodation = (item: Pick<CartItem, 'type' | 'accommodation'>): boolean =>
  item.type === ProductType.ACCOMMODATION && item.accommodation?.listing_source === 'NEXTPAX';

/**
 * Derives whether a cart item is by-request — one with no chargeable price
 * yet, settled later by an admin quote — because EITHER the listing itself
 * is by-request OR the guest's selection includes a by-request modifier on
 * an otherwise-priced listing (`hasByRequestModifier`). SERVICE,
 * ACCOMMODATION, and PACKAGE items alike defer the listing check entirely to
 * a selected child variant's/tier's own flag when one is selected, else the
 * parent's flag — the variant/tier overrides the parent in both directions
 * (a priced child under a by-request parent is NOT by-request; a by-request
 * child under a priced parent IS), the identical variant-overrides-parent
 * rule this file applies for pricing and billing basis.
 * `hasByRequestModifier` applies to SERVICE, ACCOMMODATION, and PACKAGE
 * identically (it is a fact about the selection, not the listing, so there
 * is no variant/parent distinction to make for it).
 *
 * The single policy point for this decision: `addToCart` calls it to stamp
 * `CartItem.isByRequest` at add time, and `CartButton.isDataValid` calls the
 * exact same function to decide whether the price-less by-request affordance
 * can be enabled before a price is ever computed — so the two can never
 * drift apart.
 */
export const deriveCartItemIsByRequest = (
  item: Pick<
    CartItem,
    | 'type'
    | 'service'
    | 'variant'
    | 'accommodation'
    | 'accommodationVariant'
    | 'package'
    | 'packageVariant'
    | 'hasByRequestModifier'
  >,
): boolean =>
  item.type === ProductType.SERVICE
    ? (item.variant ? item.variant.is_by_request : item.service?.is_by_request) === true ||
      item.hasByRequestModifier === true
    : item.type === ProductType.ACCOMMODATION
      ? (item.accommodationVariant
          ? item.accommodationVariant.is_by_request
          : item.accommodation?.is_by_request) === true || item.hasByRequestModifier === true
      : item.type === ProductType.PACKAGE
        ? (item.packageVariant
            ? item.packageVariant.is_by_request
            : item.package?.is_by_request) === true || item.hasByRequestModifier === true
        : false;

export interface ChargeTimingGroups {
  /** NextPax property items — charged at checkout (captured immediately). */
  today: CartItem[];
  /** Priced native accommodation + service + package items — charged
   *  off-session on confirmation. Never includes a by-request line — see
   *  `awaitingQuote`. */
  onConfirm: CartItem[];
  /** By-request accommodation + service + package items — no price yet; an
   *  admin quote settles the charge later, on-session, at quote approval. */
  awaitingQuote: CartItem[];
}

/**
 * Classifies when a cart item's charge fires. The single classification
 * site for this decision — reused by `getChargeTimingGroups` below and by
 * `checkoutSummary.buildCheckoutLineItems` — so the cart and the checkout
 * summary can never disagree about a line's timing bucket. A NextPax
 * property is always `TODAY`, even in the (currently impossible) case where
 * it also carried `isByRequest: true` — NextPax capture takes precedence.
 */
export const deriveChargeTiming = (
  item: Pick<CartItem, 'type' | 'accommodation' | 'isByRequest'>,
): ChargeTiming => {
  if (isNextPaxAccommodation(item)) return 'TODAY';
  if (item.isByRequest) return 'ON_QUOTE';
  return 'ON_CONFIRM';
};

interface CartContextType {
  cartItems: CartItem[];
  /** Returns true when the item was added or updated, false when the cart
   *  refused it (the refusal is already surfaced to the guest). */
  addToCart: (item: Omit<CartItem, 'id' | 'dateAdded' | 'isByRequest'>) => boolean;
  removeFromCart: (itemId: string) => void;
  updateCartItem: (itemId: string, updates: Partial<CartItem>) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getItemCount: () => number;
  getChargeTimingGroups: () => ChargeTimingGroups;
  isPackageInCart: (packageId: number, startDate: string, variantId?: number) => boolean;
  isCartDrawerOpen: boolean;
  openCartDrawer: () => void;
  closeCartDrawer: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const CART_STORAGE_KEY = 'belhop_cart';

/**
 * Builds the identity key for a SERVICE cart line. Variant and transfer
 * direction both join the key so two distinct combinations (e.g. both legs
 * of the same route on the same date/time) never collide into one cart
 * line. Exported so `CartButton` builds the exact same key for cart-line
 * lookup and removal — the only two construction sites for this id, and
 * they must never drift apart.
 */
/**
 * Builds the identity key for an ACCOMMODATION cart line. The variant
 * segment is appended ONLY when a variant is present — omitted entirely
 * otherwise — so a pre-existing (pre-variant-selector) localStorage cart
 * line built without a variant still resolves to the exact same id after
 * deploy. Exported so `CartButton` builds the exact same key for cart-line
 * lookup and removal — the only two construction sites for this id
 * (alongside `generateCartItemId` above), and they must never drift apart.
 */
export const buildAccommodationCartItemId = (
  accommodationId: number,
  checkInDate: string,
  checkOutDate: string,
  variantId?: number,
): string => {
  const variantSegment = variantId != null ? `-v${variantId}` : '';
  return `acc-${accommodationId}${variantSegment}-${checkInDate}-${checkOutDate}`;
};

/** What, beyond the service id, tells two service cart lines apart. */
export interface ServiceCartItemIdOptions {
  variantId?: number;
  transferDirection?: TransferDirectionValue;
  /** Undated lines are never merged: every add is its own line. */
  isUndated?: boolean;
  /** The chosen occurrence of a fixed-schedule service, standing in for date and time. */
  occurrenceId?: number;
}

export const buildServiceCartItemId = (
  serviceId: number,
  serviceDate: string,
  serviceTime: string,
  { variantId, transferDirection, isUndated = false, occurrenceId }: ServiceCartItemIdOptions = {},
): string => {
  const variantSegment = variantId ?? 0;
  const directionSegment = transferDirection ? `-${transferDirection}` : '';
  // An undated line has no date or time to tell two adds apart, so a unique
  // segment stands in for them: every add is its own line. A fixed-schedule
  // line is told apart by its occurrence instead.
  const scheduleSegment = isUndated
    ? `undated-${crypto.randomUUID()}`
    : occurrenceId !== undefined
      ? `occ-${occurrenceId}`
      : `${serviceDate}-${serviceTime}`;
  return `svc-${serviceId}-v${variantSegment}-${scheduleSegment}${directionSegment}`;
};

/**
 * Builds the identity key for a PACKAGE cart line. The variant segment is
 * appended ONLY when a variant is present — omitted entirely otherwise — so
 * a pre-existing (pre-tier-selector) localStorage cart line built without a
 * tier still resolves to the exact same id after deploy, mirroring
 * `buildAccommodationCartItemId`'s identical convention. Exported so
 * `isPackageInCart` and `generateCartItemId` build the exact same id — the
 * only two construction sites for this id, and they must never drift apart.
 */
export const buildPackageCartItemId = (
  packageId: number,
  startDate: string,
  variantId?: number,
): string => {
  const variantSegment = variantId != null ? `-v${variantId}` : '';
  return `pkg-${packageId}${variantSegment}-${startDate}`;
};

// A metered-billed service is charged by actual usage against a single
// rate-and-cap consent specific to that one line -- there is no coherent
// way to combine it with any other line's charge, in either direction, so
// the cart refuses the mix pre-emptively. The backend cart-checkout gate
// re-checks the same rule and is the authoritative enforcement point; this
// is UX only, to avoid a guest filling out an entire checkout only to be
// rejected at the very end.
const METERED_MIX_REFUSED_MESSAGE =
  'This service is billed by usage and can only be booked on its own. Please check out or remove the other items before adding it.';

// The opposite direction: the cart already holds the metered line and the
// guest is adding something else.
const METERED_LINE_IN_CART_REFUSED_MESSAGE =
  "You already have a service billed by usage in your cart, so this can't be added. Check out or remove it first.";

// The two wire values the backend accepts after the hub-relative rename
// (2026-09-22). A persisted line carrying anything else predates the rename
// and must be dropped at hydration, not translated: it would 422 at
// checkout, and before that would render the opposite route under the
// current hub/direction table — and the 24h cart expiry retires this
// branch on its own the day after deploy, making a remap table dead code.
const CURRENT_TRANSFER_DIRECTION_VALUES: TransferDirectionValue[] = ['to_hub', 'from_hub'];

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const { addNotification } = useNotifications();

  // Load cart from localStorage on initialization
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        // Filter out expired items (older than 24 hours). NextPax
        // accommodations are retained — mixed carts are supported. Also
        // drops a service line whose transferDirection predates the
        // hub-relative rename (see CURRENT_TRANSFER_DIRECTION_VALUES) —
        // absence of transferDirection is not staleness, so every
        // non-transfer line is untouched by this check.
        const validItems = parsedCart.filter((item: CartItem) => {
          const dateAdded = new Date(item.dateAdded);
          const now = new Date();
          const hoursSinceAdded = (now.getTime() - dateAdded.getTime()) / (1000 * 60 * 60);
          if (hoursSinceAdded >= 24) {
            return false;
          }
          if (
            item.transferDirection &&
            !CURRENT_TRANSFER_DIRECTION_VALUES.includes(item.transferDirection)
          ) {
            return false;
          }
          return true;
        });

        // A row persisted before `isByRequest` existed carries no such key
        // at all; normalise it to `false` (never by-request) rather than
        // leaving it `undefined`, so every reader downstream of hydration
        // can rely on a real boolean.
        const normalizedItems: CartItem[] = validItems.map((item: CartItem) => ({
          ...item,
          isByRequest: item.isByRequest ?? false,
        }));

        setCartItems(normalizedItems);

        // Persist the post-expiry state back so a refresh does not
        // re-evaluate already-dropped items.
        if (normalizedItems.length !== parsedCart.length) {
          localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(normalizedItems));
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      localStorage.removeItem(CART_STORAGE_KEY);
    }
    // The mount-time load must run exactly once per provider lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  const generateCartItemId = (item: Omit<CartItem, 'id' | 'dateAdded'>): string => {
    if (item.type === ProductType.ACCOMMODATION && item.accommodation) {
      return buildAccommodationCartItemId(
        item.accommodation.id,
        item.checkInDate ?? '',
        item.checkOutDate ?? '',
        item.accommodationVariant?.id,
      );
    } else if (item.type === ProductType.SERVICE && item.service) {
      return buildServiceCartItemId(
        item.service.id,
        item.serviceDate ?? '',
        item.serviceTime ?? '',
        {
          variantId: item.variant?.id,
          transferDirection: item.transferDirection,
          isUndated: isUndatedService(item.service),
          occurrenceId: item.occurrence?.id,
        },
      );
    } else if (item.type === ProductType.PACKAGE && item.package) {
      // Book the selected child tier when present; fall back to the parent
      // package id for tier-less items — mirrors the identical
      // variant/parent fallback used for accommodations and services above.
      return buildPackageCartItemId(
        item.package.id,
        item.checkInDate ?? '',
        item.packageVariant?.id,
      );
    }
    return `unknown-${Date.now()}`;
  };

  const addToCart = (item: Omit<CartItem, 'id' | 'dateAdded' | 'isByRequest'>): boolean => {
    const newItemId = generateCartItemId(item);
    // Derived from the bookable row's own billing basis (the selected option
    // overrides its parent), not from whatever the caller passed on
    // `item.billingBasis` (if anything). Every non-service item
    // (accommodation, package) stays undefined.
    const resolvedBillingBasis: BillingBasis | undefined =
      item.type === ProductType.SERVICE
        ? item.variant
          ? item.variant.billing_basis
          : item.service?.billing_basis
        : undefined;

    // Derived from the listing's own by-request flag — never accepted from
    // the caller (see the `CartItem.isByRequest` field doc and
    // `deriveCartItemIsByRequest`'s own doc above).
    const resolvedIsByRequest: boolean = deriveCartItemIsByRequest(item);

    // A by-request line has no chargeable price yet — an admin quote
    // settles it later — so the stored total is forced to null regardless
    // of whatever price the caller computed before this check ran.
    const resolvedTotalPrice: number | null = resolvedIsByRequest ? null : item.totalPrice;

    // Metered cart-mix guard (pre-emptive UX only; the backend gate is
    // authoritative). Skipped entirely when this add is really an update to
    // the line already occupying `newItemId` — re-adding the same line to
    // change its quantity or price is not a mix.
    const isUpdatingExistingLine = cartItems.some((cartItem) => cartItem.id === newItemId);
    if (!isUpdatingExistingLine) {
      const cartAlreadyHasMeteredLine = cartItems.some(
        (cartItem) => cartItem.billingBasis === 'METERED',
      );
      const addingMeteredLine = resolvedBillingBasis === 'METERED';
      if (cartItems.length > 0 && (cartAlreadyHasMeteredLine || addingMeteredLine)) {
        addNotification(
          cartAlreadyHasMeteredLine
            ? METERED_LINE_IN_CART_REFUSED_MESSAGE
            : METERED_MIX_REFUSED_MESSAGE,
          'error',
        );
        return false;
      }
    }

    setCartItems((prev) => {
      // Enforce one NextPax property per cart: a new NextPax property evicts
      // any other NextPax property already in the cart (the mixed-cart
      // contract carries exactly one property leg).
      let base = prev;
      if (isNextPaxAccommodation(item)) {
        base = prev.filter(
          (cartItem) => !(isNextPaxAccommodation(cartItem) && cartItem.id !== newItemId),
        );
      }

      const existingItemIndex = base.findIndex((cartItem) => cartItem.id === newItemId);
      if (existingItemIndex >= 0) {
        const updatedItems = [...base];
        updatedItems[existingItemIndex] = {
          ...item,
          totalPrice: resolvedTotalPrice,
          isByRequest: resolvedIsByRequest,
          billingBasis: resolvedBillingBasis,
          id: newItemId,
          dateAdded: base[existingItemIndex].dateAdded,
        };
        return updatedItems;
      }

      return [
        ...base,
        {
          ...item,
          totalPrice: resolvedTotalPrice,
          isByRequest: resolvedIsByRequest,
          billingBasis: resolvedBillingBasis,
          id: newItemId,
          dateAdded: new Date().toISOString(),
        },
      ];
    });
    return true;
  };

  const removeFromCart = (itemId: string): void => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const updateCartItem = (itemId: string, updates: Partial<CartItem>): void => {
    setCartItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, ...updates } : item)),
    );
  };

  const clearCart = (): void => {
    setCartItems([]);
  };

  const getTotalPrice = (): number => {
    // A by-request line has no chargeable price yet — excluded entirely
    // rather than coerced to 0-and-summed, so a mixed cart's total reflects
    // only what will actually be charged.
    return cartItems
      .filter((item) => !item.isByRequest)
      .reduce((total, item) => total + (item.totalPrice ?? 0), 0);
  };

  const getItemCount = (): number => {
    return cartItems.length;
  };

  /**
   * Bucket cart items by when the charge fires.
   * - `today`: NextPax property items — captured at checkout.
   * - `onConfirm`: priced native accommodations, services, and packages —
   *   charged off-session when the host/vendor confirms, after any price
   *   change has been shown.
   * - `awaitingQuote`: by-request accommodations, services, and packages —
   *   no price yet; an admin quote settles the charge later, on-session.
   */
  const getChargeTimingGroups = (): ChargeTimingGroups => ({
    today: cartItems.filter((item) => deriveChargeTiming(item) === 'TODAY'),
    onConfirm: cartItems.filter((item) => deriveChargeTiming(item) === 'ON_CONFIRM'),
    awaitingQuote: cartItems.filter((item) => deriveChargeTiming(item) === 'ON_QUOTE'),
  });

  const isPackageInCart = (packageId: number, startDate: string, variantId?: number): boolean => {
    const itemId = buildPackageCartItemId(packageId, startDate, variantId);
    return cartItems.some((item) => item.id === itemId);
  };

  const openCartDrawer = (): void => {
    setIsCartDrawerOpen(true);
  };

  const closeCartDrawer = (): void => {
    setIsCartDrawerOpen(false);
  };

  const contextValue: CartContextType = {
    cartItems,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    getTotalPrice,
    getItemCount,
    getChargeTimingGroups,
    isPackageInCart,
    isCartDrawerOpen,
    openCartDrawer,
    closeCartDrawer,
  };

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
};
